import { invalidatePolicyQueries } from '@/utils/policy-cache'
import { type MutableRefObject, type ReactNode, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  renderToMarkdown,
  renderToMarkdownReadable,
} from '@/tiptap/markdown/renderToMarkdown'
import { slugifySync } from '@deepguide-ai/shared'
import {
  ArrowLeft,
  FileText,
  Loader2,
  Shield,
  Check,
  Pencil,
  Save,
  History,
  Copy,
  RefreshCw,
  FileType,
  FileDown,
  Lock,
  MoreHorizontal,
  ClipboardList,
  Send,
  RotateCcw,
  Upload,
  GitCompareArrows,
  Sparkles,
  Trash2,
  Eraser,
} from 'lucide-react'
import { toast } from 'sonner'
import { ConfirmDialog } from '@/components/confirm-dialog'
import {
  downloadBlob,
  getFilenameFromContentDisposition,
} from '@/lib/download-file'
import { trpc } from '@/lib/trpc'
import { PolicyQuestionnaireDialog } from './PolicyQuestionnaireDialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@deepguide-ai/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'
import { Input } from '@deepguide-ai/ui/input'
import { RoleSelect, type RoleSelectRole } from '@/components/compliance/RoleSelect'
import { AskAiButton } from '@/components/chat/AskAiButton'
import type {
  GenerationItem,
  PolicyItem,
  PolicyClaimData,
} from './types'

export type PolicyDetailHeaderProps = {
  item: GenerationItem
  policyData?: PolicyItem
  programId: string
  editorRef: MutableRefObject<any>
  isEditing: boolean
  editLocked?: boolean
  editLockedByName?: string | null
  onEditingChange: (editing: boolean) => boolean | Promise<boolean>
  onSave?: () => void
  isSaving?: boolean
  onClose?: () => void
  /** Claims sidebar toggle (kept on the type so the policy detail callsites
   * can pass these through even though the header no longer renders the
   * toggle UI itself). */
  claims?: PolicyClaimData[]
  showClaims?: boolean
  onShowClaimsChange?: (show: boolean) => void
  /** Deterministic v2 regenerate — completes synchronously. */
  onRegenerateImmediate?: () => void
  /** RFC-231 §6: rewrite the prose in the company's voice, claims pinned. */
  onRewriteInOurVoice?: () => void
  isRegeneratingImmediate?: boolean
  /** Upload your own document as a new version (or first version). */
  onUploadNewVersion?: () => void
  isUploadingVersion?: boolean
  onOpenPolicyLineage?: () => void
  isPolicyLineageLoading?: boolean
  /** Version viewing */
  viewingVersion?: number | null
  onViewingVersionChange?: (version: number | null) => void
  onOpenVersionDiff?: () => void
  /** Approve */
  onApprove: () => void
  isApproving: boolean
  onBeforeRequestApproval?: () => Promise<void>
  onApprovalRequested?: () => void
  policyStatus?: string
  /** Auto-save status indicator */
  saveStatus?: 'idle' | 'saving' | 'saved'
  afterSaveStatus?: ReactNode
  /** Style: 'breadcrumb' shows ← Policies / title, 'simple' shows icon + title */
  titleStyle?: 'breadcrumb' | 'simple'
  /** Render only right-side actions (for ScreenataPageLayout headerActions), or
   * only the "..." overflow menu (for headerExtra). */
  renderMode?: 'full' | 'actions' | 'overflow'
  className?: string
  /** Hide edit/approve/regenerate actions (auditor readonly view) */
  readOnly?: boolean
  /** When set, only show this version in the version dropdown (auditor view) */
  submittedVersion?: number | null
  /** Transparent "..." trigger; background on hover only (admin auditor preview). */
  transparentOverflowMenu?: boolean
}

export function PolicyDetailHeader({
  item,
  policyData,
  programId,
  editorRef,
  isEditing,
  editLocked = false,
  editLockedByName = null,
  onEditingChange,
  onSave,
  isSaving,
  onClose,
  claims: _claims = [],
  showClaims: _showClaims,
  onShowClaimsChange: _onShowClaimsChange,
  onRegenerateImmediate,
  onRewriteInOurVoice,
  isRegeneratingImmediate = false,
  onUploadNewVersion,
  isUploadingVersion = false,
  onOpenPolicyLineage,
  isPolicyLineageLoading = false,
  viewingVersion = null,
  onViewingVersionChange,
  onOpenVersionDiff,
  onApprove,
  isApproving,
  onBeforeRequestApproval,
  onApprovalRequested,
  policyStatus,
  saveStatus,
  afterSaveStatus,
  titleStyle = 'breadcrumb',
  renderMode = 'full',
  className,
  readOnly = false,
  submittedVersion: submittedVersionProp,
  transparentOverflowMenu = false,
}: PolicyDetailHeaderProps) {
  const [showAnswers, setShowAnswers] = useState(false)
  // Always-on summary feed so we can decide whether Regenerate gates
  // behind the survey dialog without paying a round-trip on click.
  // Payload is small (one row per blueprint). The dialog itself reads
  // a richer per-blueprint feed (`getPolicyQuestionnaireBlueprints` +
  // `readinessQuestionnaire.getRowState`).
  const answersQuery = useQuery(
    trpc.complianceProgram.getPolicyAnswers.queryOptions({
      programId,
      policySlug: item.slug,
    }),
  )
  const answers = answersQuery.data?.answers ?? []
  // Gate for Regenerate: open the survey dialog whenever the policy has
  // questions the program is asked (RFC-224 leaves only values such as a
  // review cadence or RTO), so they get a second look before the policy is
  // rewritten. A policy with none regenerates immediately.
  const hasQuestions = answers.length > 0
  // The dialog footer: something still needs an answer, or a DERIVED answer
  // hasn't been confirmed yet (RFC-162).
  const reviewNeeded = answers.some(
    (a) =>
      a.required !== false &&
      (!a.answeredAt ||
        ((a as { source?: string }).source === 'DERIVED' &&
          !(a as { confirmed?: boolean }).confirmed)),
  )

  const versionsQuery = useQuery({
    ...trpc.complianceProgram.getPolicyVersions.queryOptions({
      policyDocumentId: item.policyDocumentId ?? '',
    }),
    enabled: !!item.policyDocumentId,
  })

  const versions = ((versionsQuery.data as any)?.versions ?? []) as {
    id: string
    version: number
    changeNote: string | null
    changeType?: 'material' | 'non_material' | null
    createdAt: string
    approvalSignature: {
      approvedAt: string
      signerNameSnapshot: string | null
      overrideReason?: string | null
    } | null
  }[]

  const effectiveStatus =
    policyStatus ??
    ((versionsQuery.data as any)?.policyStatus as string | undefined) ??
    item.policyStatus
  const currentVersion =
    ((versionsQuery.data as any)?.currentVersion as number | undefined) ??
    (item as any).currentVersion ??
    versions[0]?.version ??
    0
  const currentVersionEntry =
    versions.find((version) => version.version === currentVersion) ?? null
  // "Currently approved" is driven by the document's status, not the
  // per-version PolicyApprovalSignature row. Signatures are preserved as
  // immutable audit trail; unapproving a policy resets status to
  // 'generated' but does NOT delete the signature row. Using the
  // signature here would keep showing "Approved" after unapprove.
  const currentVersionApproved =
    effectiveStatus === 'approved' && !!currentVersionEntry?.approvalSignature
  const hasDraft = effectiveStatus != null && effectiveStatus !== 'approved'
  // In readonly/auditor mode, only show the submitted version
  const displayVersions = readOnly && submittedVersionProp != null
    ? versions.filter(v => v.version === submittedVersionProp)
    : versions
  const submittedVersionEntry =
    submittedVersionProp != null
      ? versions.find((version) => version.version === submittedVersionProp) ?? null
      : null
  const submittedVersionLabel =
    submittedVersionProp != null
      ? `v${submittedVersionProp}${
          submittedVersionEntry?.approvalSignature ? ' (Approved)' : ''
        }`
      : 'v0'
  // `isGenerated` is true as soon as the row leaves `not_started` — which
  // includes a policy queued or mid-generation with nothing written yet. Edit
  // would open an empty editor whose save races the writer, and Request
  // Approval would ask someone to approve nothing, so the write actions wait
  // for the draft to land (on a regenerate the old content stays readable,
  // but editing it would still be clobbered by the incoming write).
  const isDraftingInBackground =
    item.generationState === 'queued' || item.generationState === 'generating'
  // `isGenerated` is also true for a row merely *enqueued* — including one
  // whose job was lost, which leaves `status = 'queued'` set forever. Require
  // a real version too, so a policy with nothing written never offers Edit
  // (an empty editor) or Request Approval (approving nothing).
  const hasContent =
    item.isGenerated && !isDraftingInBackground && currentVersion > 0
  const showActions = hasContent
  const showEditActions = showActions && !readOnly
  const showVersionDropdown =
    displayVersions.length > 0 && onViewingVersionChange
  const showVersionDiffButton =
    showVersionDropdown && displayVersions.length > 1 && !!onOpenVersionDiff
  const canApprove =
    showEditActions && !!item.policyDocumentId && hasDraft && !editLocked
  const showTitle = renderMode === 'full'
  const actionButtonClass =
    'inline-flex h-7 items-center gap-1.5 rounded-sm px-2.5 text-[12px] leading-4 font-medium transition-colors'
  const editLockTooltip = editLockedByName
    ? `${editLockedByName} is editing this policy. Editing is locked until they finish.`
    : 'Another user is editing this policy. Editing is locked until they finish.'

  const pdfDownloadMutation = useMutation(
    trpc.complianceProgram.getPolicyPdfDownloadLink.mutationOptions()
  )
  const docxDownloadMutation = useMutation(
    trpc.complianceProgram.getPolicyDocxDownloadLink.mutationOptions()
  )
  const requestApprovalMutation = useMutation(
    trpc.complianceProgram.requestPolicyApproval.mutationOptions()
  )
  const returnForChangesMutation = useMutation(
    trpc.complianceProgram.returnPolicyForChanges.mutationOptions()
  )
  const updateMetadataMutation = useMutation(
    trpc.complianceProgram.updatePolicyMetadata.mutationOptions()
  )
  const updateDisplayTitleMutation = useMutation(
    trpc.complianceProgram.updatePolicyDisplayTitle.mutationOptions()
  )
  const deletePolicyMutation = useMutation(
    trpc.complianceProgram.deletePolicy.mutationOptions()
  )
  const discardDraftMutation = useMutation(
    trpc.complianceProgram.discardPolicyDraft.mutationOptions()
  )
  const isApproved = !!policyData?.approvedAt
  const queryClient = useQueryClient()
  const [returnDialogOpen, setReturnDialogOpen] = useState(false)
  const [returnReason, setReturnReason] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false)
  // Regenerating replaces the current content with a fresh draft. On an
  // approved or uploaded policy that is a real loss (the prior text survives
  // only in version history), so it gets its own confirmation — the answers
  // dialog only reviews inputs, it never says "this overwrites".
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false)
  const isImportedSource =
    policyData?.source === 'IMPORTED' || policyData?.source === 'IMPORTED_THEN_AI'
  const regenerateReplaces = effectiveStatus === 'approved' || isImportedSource
  const requestRegenerate = () => {
    if (!onRegenerateImmediate) return
    if (regenerateReplaces) setReplaceDialogOpen(true)
    else onRegenerateImmediate()
  }
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [displayTitle, setDisplayTitle] = useState(item.title)
  const [isPreparingApprovalRequest, setIsPreparingApprovalRequest] =
    useState(false)
  // Soft prompt: when requesting approval with no approver assigned, let the
  // user pick an approver role first (skippable — falls back to owner/admin).
  const [approverDialogOpen, setApproverDialogOpen] = useState(false)
  const [selectedApproverRoleId, setSelectedApproverRoleId] = useState('')
  const rolesQuery = useQuery({
    ...trpc.complianceRole.list.queryOptions(),
    enabled: approverDialogOpen,
  })
  const roles = (rolesQuery.data ?? []) as RoleSelectRole[]
  const approverDialogPending =
    isPreparingApprovalRequest ||
    requestApprovalMutation.isPending ||
    updateMetadataMutation.isPending

  // The workflow state controls which action buttons render. Prefer the
  // explicit `policyData.status` (extended PolicyItem) when available, then
  // fall back to the inferred string from effectiveStatus / item.policyStatus
  // for legacy callers that haven't refetched.
  const workflowStatus = policyData?.status ?? effectiveStatus ?? item.policyStatus
  const isPendingApproval = workflowStatus === 'pending_approval'
  const approverRoleId = policyData?.approverRoleId ?? null
  const approverRoleName = policyData?.approverRoleName ?? null
  const approvalRequestedByName = policyData?.approvalRequestedByName ?? null

  const invalidateApprovalQueries = async () => {
    await invalidatePolicyQueries(queryClient)
    if (item.policyDocumentId) {
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyVersions.queryKey({
          policyDocumentId: item.policyDocumentId,
        }),
      })
    }
  }

  // RFC-191 §6.9 boundary: a custom policy is removable outright, a catalog
  // slot never is — its draft can only be reset to ungenerated, because the
  // framework requirement survives the content. The two actions are therefore
  // mutually exclusive, gated on the same `deletable` flag the server enforces.
  // `deletable` only means "not a catalog slug" — it says nothing about
  // approval, and the server's deletePolicy doesn't check either. An approved
  // policy is a signed audit artifact, so hold it to the same bar discardDraft
  // already enforces: unapprove first.
  const canDeletePolicy =
    !readOnly &&
    !!item.deletable &&
    !!item.policyDocumentId &&
    !isApproved &&
    item.generationState !== 'approved'
  const canDiscardDraft =
    !readOnly &&
    !item.deletable &&
    item.isGenerated &&
    !item.coveredByDocumentId &&
    item.generationState !== 'approved' &&
    !!item.policyDocumentId

  const handleDeletePolicy = async () => {
    if (!item.policyDocumentId) return
    try {
      await deletePolicyMutation.mutateAsync({
        policyDocumentId: item.policyDocumentId,
      })
      await invalidatePolicyQueries(queryClient)
      setDeleteDialogOpen(false)
      toast.success(`Deleted "${item.title}"`)
      // The policy this page renders no longer exists — leave before the
      // detail queries refetch against a dead id.
      onClose?.()
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to delete policy'
      )
    }
  }

  const handleDiscardDraft = async () => {
    if (!item.policyDocumentId) return
    try {
      await discardDraftMutation.mutateAsync({
        policyDocumentId: item.policyDocumentId,
      })
      await Promise.all([
        invalidatePolicyQueries(queryClient),
        queryClient.invalidateQueries({
          queryKey:
            trpc.complianceProgram.getPolicyRequirementCoverage.queryKey(),
        }),
      ])
      setDiscardDialogOpen(false)
      toast.success('Draft discarded')
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to discard draft'
      )
    }
  }

  const openRenameDialog = () => {
    setDisplayTitle(item.title)
    setRenameDialogOpen(true)
  }

  const updateDisplayTitle = async (title: string | null) => {
    try {
      const result = await updateDisplayTitleMutation.mutateAsync({
        programId,
        policySlug: item.slug,
        title,
      })
      await Promise.all([
        invalidatePolicyQueries(queryClient),
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyVersions.queryKey({
            policyDocumentId: result.policyDocumentId,
          }),
        }),
      ])
      setRenameDialogOpen(false)
      toast.success(title === null ? 'Policy name reset' : 'Policy renamed')
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to rename policy'
      )
    }
  }

  const submitApprovalRequest = async () => {
    if (isPreparingApprovalRequest || requestApprovalMutation.isPending) return
    setIsPreparingApprovalRequest(true)
    try {
      await onBeforeRequestApproval?.()
      await requestApprovalMutation.mutateAsync({
        programId,
        policySlug: item.slug,
      })
      onApprovalRequested?.()
      await invalidateApprovalQueries()
      toast.success('Approval requested')
    } catch (err: any) {
      toast.error('Failed to request approval: ' + (err.message ?? 'Unknown error'))
    } finally {
      setIsPreparingApprovalRequest(false)
    }
  }

  // Soft prompt: if no approver is assigned, surface a picker before requesting
  // (the user can still proceed without one — it falls back to owner/admin).
  const handleRequestApprovalClick = () => {
    if (editLocked) return
    if (!approverRoleId) {
      setSelectedApproverRoleId('')
      setApproverDialogOpen(true)
      return
    }
    void submitApprovalRequest()
  }

  const handleConfirmApproverDialog = async () => {
    try {
      if (selectedApproverRoleId) {
        await updateMetadataMutation.mutateAsync({
          programId,
          policySlug: item.slug,
          approverRoleId: selectedApproverRoleId,
        })
        await invalidateApprovalQueries()
      }
      setApproverDialogOpen(false)
      await submitApprovalRequest()
    } catch (err: any) {
      toast.error('Failed to assign approver: ' + (err.message ?? 'Unknown error'))
    }
  }

  const handleReturnForChanges = async () => {
    if (!returnReason.trim()) return
    try {
      await returnForChangesMutation.mutateAsync({
        programId,
        policySlug: item.slug,
        reason: returnReason.trim(),
      })
      await invalidateApprovalQueries()
      setReturnDialogOpen(false)
      setReturnReason('')
      toast.success('Returned for changes')
    } catch (err: any) {
      toast.error('Failed to return: ' + (err.message ?? 'Unknown error'))
    }
  }

  const handleCopyMarkdown = () => {
    const editor = editorRef.current
    if (!editor) return
    try {
      const raw = renderToMarkdown(editor.getJSON())
      const markdown = raw.replace(/ ?:dg\{id="[^"]*"\}/g, '')
      navigator.clipboard.writeText(markdown)
      toast.success('Copied as Markdown')
    } catch {
      toast.error('Failed to copy')
    }
  }

  const handleMarkdownDownload = async () => {
    const editor = editorRef.current
    if (!editor) return
    try {
      const raw = renderToMarkdownReadable(editor.getJSON())
      const markdown = raw.replace(/ ?:dg\{id="[^"]*"\}/g, '')
      if (!markdown.trim()) {
        toast.error('No content available')
        return
      }
      const fileBaseName = slugifySync(item.title || '') || 'policy-document'
      const blob = new Blob([markdown], {
        type: 'text/markdown;charset=utf-8',
      })
      downloadBlob(blob, `${fileBaseName}.md`)
      toast.success('Markdown download started')
    } catch {
      toast.error('Failed to download')
    }
  }

  const handlePdfDownload = async () => {
    if (!item.policyDocumentId) return
    try {
      toast.loading('Preparing PDF...', { id: 'policy-pdf-download' })
      const result = await pdfDownloadMutation.mutateAsync({
        policyDocumentId: item.policyDocumentId,
      })
      if (!result.success || !result.downloadUrl) {
        throw new Error('Failed to get download link')
      }
      const response = await fetch(result.downloadUrl, {
        credentials: 'include',
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const blob = await response.blob()
      const filename = getFilenameFromContentDisposition(
        response.headers.get('Content-Disposition'),
        result.fileName
      )
      downloadBlob(blob, filename)
      toast.success('PDF download started', { id: 'policy-pdf-download' })
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to prepare PDF download'
      toast.error(message, { id: 'policy-pdf-download' })
    }
  }

  const handleDocxDownload = async () => {
    if (!item.policyDocumentId) return
    try {
      toast.loading('Preparing DOCX...', { id: 'policy-docx-download' })
      const result = await docxDownloadMutation.mutateAsync({
        policyDocumentId: item.policyDocumentId,
      })
      if (!result.success || !result.downloadUrl) {
        throw new Error('Failed to get download link')
      }
      const response = await fetch(result.downloadUrl, {
        credentials: 'include',
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const blob = await response.blob()
      const filename = getFilenameFromContentDisposition(
        response.headers.get('Content-Disposition'),
        result.fileName
      )
      downloadBlob(blob, filename)
      toast.success('DOCX download started', { id: 'policy-docx-download' })
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to prepare DOCX download'
      toast.error(message, { id: 'policy-docx-download' })
    }
  }

  const overflowMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={
            transparentOverflowMenu
              ? 'inline-flex h-7 w-7 items-center justify-center rounded-sm bg-transparent text-text-body transition-colors hover:bg-muted'
              : 'inline-flex h-7 w-7 items-center justify-center rounded-sm bg-card text-text-body transition-colors hover:bg-muted'
          }
        >
          <MoreHorizontal className='h-3.5 w-3.5' />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' className='z-[10010]'>
        {showVersionDiffButton && (
          <DropdownMenuItem onClick={onOpenVersionDiff}>
            <GitCompareArrows className='h-4 w-4' />
            Compare versions
          </DropdownMenuItem>
        )}
        {!readOnly && (
          <>
            {/* Upload your own document → new version (or first version). Only
                on the live current version, not while viewing history. */}
            {!isPendingApproval &&
              onUploadNewVersion &&
              viewingVersion === null && (
                <DropdownMenuItem
                  onClick={onUploadNewVersion}
                  disabled={isUploadingVersion}
                >
                  {isUploadingVersion ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <Upload className='h-4 w-4' />
                  )}
                  {isUploadingVersion ? 'Uploading…' : 'Upload your own version'}
                </DropdownMenuItem>
              )}
            <DropdownMenuItem onClick={handleCopyMarkdown}>
              <Copy className='h-4 w-4' />
              Copy as Markdown
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          onClick={() => {
            void handleMarkdownDownload()
          }}
        >
          <FileText className='h-4 w-4' />
          Download Markdown
        </DropdownMenuItem>
        {item.policyDocumentId && (
          <DropdownMenuItem
            onClick={() => {
              void handleDocxDownload()
            }}
            disabled={docxDownloadMutation.isPending}
          >
            <FileDown className='h-4 w-4' />
            Download DOCX
          </DropdownMenuItem>
        )}
        {isApproved && item.policyDocumentId && (
          <DropdownMenuItem
            onClick={() => {
              void handlePdfDownload()
            }}
            disabled={pdfDownloadMutation.isPending}
          >
            <FileType className='h-4 w-4' />
            Download PDF
          </DropdownMenuItem>
        )}
        {showActions &&
          viewingVersion === null &&
          onOpenPolicyLineage && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onOpenPolicyLineage}
                disabled={isPolicyLineageLoading}
              >
                {isPolicyLineageLoading ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <Shield className='h-4 w-4' />
                )}
                Policy lineage
              </DropdownMenuItem>
            </>
          )}
        {!readOnly && onRegenerateImmediate && (
          <>
            {!isPendingApproval &&<DropdownMenuSeparator />}
            {!isPendingApproval && (
              <DropdownMenuItem onClick={() => setShowAnswers(true)}>
                <ClipboardList className='h-4 w-4' />
                Review answers
              </DropdownMenuItem>
            )}
            {!isPendingApproval && (
              <DropdownMenuItem
                onClick={() => {
                  // Gate: surface the source-answers dialog whenever the
                  // policy has questions, so they get a second look.
                  // Otherwise regenerate immediately.
                  if (hasQuestions) {
                    setShowAnswers(true)
                  } else {
                    requestRegenerate()
                  }
                }}
                disabled={isRegeneratingImmediate}
              >
                {isRegeneratingImmediate ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <RefreshCw className='h-4 w-4' />
                )}
                Regenerate
              </DropdownMenuItem>
            )}
            {!isPendingApproval && onRewriteInOurVoice && (
              <DropdownMenuItem
                onClick={onRewriteInOurVoice}
                disabled={isRegeneratingImmediate}
                title='Rewrites the framing prose only; every claim sentence stays verbatim and is re-checked.'
              >
                <Sparkles className='h-4 w-4' />
                Write it in our voice
              </DropdownMenuItem>
            )}
          </>
        )}
        {/* Destructive actions live last, behind the overflow, on the detail
            page you'd be deleting from — never on a list row where one stray
            click destroys a policy you weren't looking at. */}
        {(canDiscardDraft || canDeletePolicy) && viewingVersion === null && (
          <>
            <DropdownMenuSeparator />
            {canDiscardDraft && (
              <DropdownMenuItem
                onClick={() => setDiscardDialogOpen(true)}
                className='text-destructive focus:text-destructive'
              >
                <Eraser className='h-4 w-4' />
                Discard draft
              </DropdownMenuItem>
            )}
            {canDeletePolicy && (
              <DropdownMenuItem
                onClick={() => setDeleteDialogOpen(true)}
                className='text-destructive focus:text-destructive'
              >
                <Trash2 className='h-4 w-4' />
                Delete policy
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const showPendingApprovalPill =
    canApprove &&
    viewingVersion === null &&
    workflowStatus === 'pending_approval'
  const editLockIndicator = editLocked ? (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label='Policy editing locked'
            className='inline-flex h-6 w-6 shrink-0 cursor-help items-center justify-center rounded-sm border border-status-red/30 bg-status-red-bg text-status-red'
          >
            <Lock className='h-3.5 w-3.5' />
          </span>
        </TooltipTrigger>
        <TooltipContent side='bottom' align='start'>
          {editLockTooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null

  const renameDisabled =
    isEditing || !!isSaving || editLocked || updateDisplayTitleMutation.isPending
  const renameButton = !readOnly ? (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type='button'
            aria-label='Rename policy'
            onClick={openRenameDialog}
            disabled={renameDisabled}
            className='inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-text-subtle transition-colors hover:bg-muted hover:text-text-body disabled:cursor-not-allowed disabled:opacity-40'
          >
            <Pencil className='h-3.5 w-3.5' />
          </button>
        </TooltipTrigger>
        <TooltipContent side='bottom'>Rename policy</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null

  const actions = (
    <div className='flex items-center gap-2'>
        {showPendingApprovalPill && (
          <span
            className='inline-flex h-6 shrink-0 cursor-default select-none items-center gap-1 rounded-full bg-indigo-50 px-2.5 text-[11px] font-medium text-indigo-700'
            title={
              approvalRequestedByName
                ? `Requested by ${approvalRequestedByName}`
                : undefined
            }
          >
            {/* <Loader2 className='h-3 w-3 shrink-0' /> */}
            {approverRoleName
              ? `Pending ${approverRoleName} approval`
              : 'Pending approval'}
          </span>
        )}

        {/* Auto-save status */}
        {saveStatus === 'saving' && (
          <span className='inline-flex items-center gap-1.5 text-[11px] text-text-placeholder'>
            <Loader2 className='h-3 w-3 animate-spin' />
            Saving...
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className='inline-flex items-center gap-1.5 text-[11px] text-text-placeholder'>
            <Check className='h-3 w-3' />
            Saved
          </span>
        )}

        {afterSaveStatus}

        {/* Background drafting pill — covers both empty (no versions yet) and
            refining (existing draft, iteration loop still running). Replaces
            the in-editor "Refining draft" banner so the editor area stays
            uncluttered. */}
        {!readOnly && (item.generationState === 'queued' ||
          item.generationState === 'generating') && (
            <span
              className={`inline-flex h-7 items-center gap-1.5 rounded-sm border border-border bg-card px-2 text-[12px] font-medium ${
                item.generationState === 'generating'
                  ? 'text-cobalt-strong'
                  : 'text-text-subtle'
              }`}
            >
              {item.generationState === 'generating' ? (
                <Loader2 className='h-3 w-3 shrink-0 animate-spin' />
              ) : (
                <History className='h-3 w-3 shrink-0 text-text-placeholder' />
              )}
              {item.generationState === 'generating'
                ? displayVersions.length > 0
                  ? 'Refining draft'
                  : 'Generating'
                : 'Queued'}
            </span>
          )}

        {/* Version dropdown */}
        {showVersionDropdown && (
          <Select
            value={
              viewingVersion !== null
                ? String(viewingVersion)
                : readOnly && submittedVersionProp != null
                  ? String(submittedVersionProp)
                  : 'current'
            }
            onValueChange={(v) => {
              void (async () => {
                const canLeaveEditor = await onEditingChange(false)
                if (!canLeaveEditor) return
                onViewingVersionChange(v === 'current' ? null : Number(v))
              })()
            }}
          >
            <SelectTrigger
              className={`h-7 w-auto gap-1.5 border-border bg-card px-2 text-[12px] font-medium shadow-none ${
                item.generationState === 'generating'
                  ? 'text-cobalt-strong'
                  : item.generationState === 'queued'
                    ? 'text-text-subtle'
                    : 'text-text-body'
              }`}
            >
              {item.generationState === 'generating' ? (
                <Loader2 className='h-3 w-3 shrink-0 animate-spin' />
              ) : (
                <History className='h-3 w-3 shrink-0 text-text-placeholder' />
              )}
              <span className='truncate'>
                {viewingVersion !== null
                  ? `v${viewingVersion}${versions.find((v) => v.version === viewingVersion)?.approvalSignature ? ' (Approved)' : ' (Draft)'}`
                  : readOnly && submittedVersionProp != null
                    ? submittedVersionLabel
                  : item.generationState === 'generating' && currentVersion > 0
                    ? `v${currentVersion} (Generating)`
                    : item.generationState === 'queued' && currentVersion > 0
                      ? `v${currentVersion} (Queued)`
                      : currentVersion > 0
                        ? `v${currentVersion}${hasDraft ? ' (Draft)' : currentVersionApproved ? ' (Approved)' : ''}`
                        : 'v0'}
              </span>
            </SelectTrigger>
            <SelectContent className='max-w-[400px]'>
              {displayVersions.map((v) => {
                const isCurrentVersion = v.version === currentVersion
                const isDraft = !v.approvalSignature
                return (
                  <SelectItem
                    key={v.id}
                    value={
                      !readOnly && isCurrentVersion
                        ? 'current'
                        : String(v.version)
                    }
                    className='text-[12px]'
                  >
                    <div className='flex items-baseline gap-1.5'>
                      <span className='shrink-0 font-medium'>v{v.version}</span>
                      <span className='shrink-0 text-text-placeholder'>
                        {new Date(v.createdAt).toLocaleString(undefined, {
                          month: 'numeric',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                      {v.approvalSignature ? (
                        <>
                          <span className='shrink-0 rounded-sm bg-green-50 px-1 py-0.5 text-[10px] font-medium text-green-700 dark:bg-status-green-bg dark:text-status-green-strong'>
                            Approved
                            {v.approvalSignature.signerNameSnapshot
                              ? ` by ${v.approvalSignature.signerNameSnapshot}`
                              : ''}
                          </span>
                          {v.approvalSignature.overrideReason ? (
                            <span className='shrink-0 rounded-sm bg-amber-50 px-1 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-status-orange-bg dark:text-status-orange-strong'>
                              Known gaps
                            </span>
                          ) : null}
                          {v.changeType === 'material' ? (
                            <span className='shrink-0 rounded-sm bg-purple-50 px-1 py-0.5 text-[10px] font-medium text-status-purple dark:bg-status-purple-bg dark:text-status-purple-strong'>
                              Material change
                            </span>
                          ) : v.changeType === 'non_material' ? (
                            <span className='shrink-0 rounded-sm bg-gray-100 px-1 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-muted dark:text-muted-foreground'>
                              Non-material
                            </span>
                          ) : null}
                        </>
                      ) : isDraft ? (
                        <span className='shrink-0 rounded-sm bg-amber-50 px-1 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-status-orange-bg dark:text-status-orange-strong'>
                          Draft
                        </span>
                      ) : (v as any).createdByName ? (
                        <span className='text-text-subtle'>
                          by {(v as any).createdByName}
                        </span>
                      ) : null}
                    </div>
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        )}

        {showEditActions &&
          !isPendingApproval &&
          !isEditing &&
          !editLocked &&
          !!item.policyDocumentId && (
            <button
              type='button'
              data-tour='policy-edit-button'
              onClick={() => {
                void onEditingChange(true)
              }}
              className={`${actionButtonClass} border border-border bg-card text-text-body hover:bg-muted`}
            >
              <Pencil className='h-3.5 w-3.5' />
              Edit
            </button>
          )}

        {/* Approval workflow. `canApprove` already gates on "has policyDoc +
            current draft + not readonly". The state machine then picks
            which buttons to show. Permission is enforced by the server. */}
        {canApprove && viewingVersion === null && (
          <>
            {workflowStatus === 'pending_approval' ? (
              // Approve / Return only render for users who may actually act on
              // the approval (owner, admin, or the assigned approver). Everyone
              // else just sees the "Pending approval" pill above. Server
              // enforces the same rule via `assertCanApprovePolicy`.
              policyData?.canApprove && (
                <>
                  <button
                    onClick={onApprove}
                    disabled={isApproving}
                    className={`${actionButtonClass} border border-status-green bg-status-green text-white hover:bg-[#246E32] disabled:opacity-50`}
                  >
                    {isApproving ? (
                      <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    ) : (
                      <Check className='h-3.5 w-3.5' />
                    )}
                    Approve
                  </button>
                  <button
                    onClick={() => setReturnDialogOpen(true)}
                    disabled={returnForChangesMutation.isPending}
                    className={`${actionButtonClass} border border-border bg-card text-text-body hover:bg-muted disabled:opacity-50`}
                  >
                    <RotateCcw className='h-3.5 w-3.5' />
                    Return for Changes
                  </button>
                </>
              )
            ) : (
              <button
                // Coach-mark anchor (issue #1349): the /policy tour's
                // submit-for-review step. Only rendered for an editable draft,
                // so DOM presence encodes the "draft vs approved" state gate.
                data-tour='policy-request-approval'
                onClick={handleRequestApprovalClick}
                disabled={approverDialogPending}
                className={`${actionButtonClass} border border-primary bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {isPreparingApprovalRequest ||
                requestApprovalMutation.isPending ? (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                ) : (
                  <Send className='h-3.5 w-3.5' />
                )}
                Request Approval
              </button>
            )}
          </>
        )}

        {renderMode === 'full' && <AskAiButton variant='header' />}

        {renderMode === 'full' && overflowMenu}
    </div>
  )

  return (
    <div
      className={
        renderMode === 'full'
          ? `flex h-[50px] shrink-0 items-center justify-between border-b border-border px-5 py-3 ${className || ''}`
          : className ?? ''
      }
    >
      {/* Left: title */}
      {showTitle ? (
        <div className='flex min-w-0 items-center gap-2'>
          {titleStyle === 'breadcrumb' ? (
            <>
              <button
                onClick={onClose}
                className='inline-flex items-center gap-1 rounded-sm px-1.5 py-1 text-[13px] leading-4 text-text-subtle transition-colors hover:bg-muted hover:text-text-secondary'
              >
                <ArrowLeft className='h-3.5 w-3.5' />
                Policies
              </button>
              <span className='text-border-divider'>/</span>
              <span className='font-heading truncate text-[14px] font-semibold text-foreground'>
                {item.title}
              </span>
              {renameButton}
              {editLockIndicator}
            </>
          ) : (
            <>
              <FileText className='h-4 w-4 shrink-0 text-cobalt-strong' />
              <h2 className='font-heading truncate text-[14px] font-semibold text-foreground'>
                {item.title}
              </h2>
              {renameButton}
              {editLockIndicator}
            </>
          )}
        </div>
      ) : null}

      {/* Right: actions */}
      {renderMode === 'overflow' ? (
        <div className='flex items-center gap-0.5'>
          {renameButton}
          {overflowMenu}
        </div>
      ) : actions}

      <PolicyQuestionnaireDialog
        programId={programId}
        policySlug={item.slug}
        policyTitle={item.title}
        open={showAnswers && !isPendingApproval}
        onOpenChange={setShowAnswers}
        onRegenerate={isPendingApproval ? undefined : requestRegenerate}
        isRegenerating={isRegeneratingImmediate}
        readOnly={readOnly || isPendingApproval}
        reviewNeeded={reviewNeeded}
      />

      <ConfirmDialog
        open={replaceDialogOpen}
        onOpenChange={setReplaceDialogOpen}
        title='Regenerate and replace the current version?'
        desc={
          <span>
            <span className='text-text-secondary font-medium'>{item.title}</span>{' '}
            {effectiveStatus === 'approved' ? 'is approved' : 'has content'}
            {isImportedSource
              ? effectiveStatus === 'approved'
                ? ' and its current text is your uploaded document'
                : ' that is your uploaded document'
              : ''}
            . Regenerating writes a new draft from the recorded answers and
            replaces the current content
            {currentVersion > 0 ? ` (v${currentVersion})` : ''}
            {effectiveStatus === 'approved' ? ', which will need approval again' : ''}
            . The current version stays in history.
          </span>
        }
        destructive
        confirmText='Regenerate and replace'
        isLoading={isRegeneratingImmediate}
        handleConfirm={() => {
          setReplaceDialogOpen(false)
          onRegenerateImmediate?.()
        }}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title='Delete this policy?'
        desc={
          <span>
            <span className='text-text-secondary font-medium'>{item.title}</span>{' '}
            will be permanently deleted, along with its content and version
            history. This can&apos;t be undone.
          </span>
        }
        destructive
        confirmText='Delete policy'
        isLoading={deletePolicyMutation.isPending}
        handleConfirm={handleDeletePolicy}
      />

      <ConfirmDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        title='Discard this draft?'
        desc={
          <span>
            The current draft of{' '}
            <span className='text-text-secondary font-medium'>{item.title}</span>{' '}
            will be cleared and the policy returns to ungenerated. The
            requirement stays. You can regenerate or upload a new document
            afterwards.
          </span>
        }
        destructive
        confirmText='Discard draft'
        isLoading={discardDraftMutation.isPending}
        handleConfirm={handleDiscardDraft}
      />

      <ConfirmDialog
        open={returnDialogOpen}
        onOpenChange={(open) => {
          setReturnDialogOpen(open)
          if (!open) setReturnReason('')
        }}
        title='Return policy for changes'
        desc='The requester will be notified with the reason you provide.'
        confirmText='Return for Changes'
        handleConfirm={handleReturnForChanges}
        isLoading={returnForChangesMutation.isPending}
        showInput
        inputValue={returnReason}
        onInputChange={setReturnReason}
        inputRequired
        inputPlaceholder='What needs to change before this policy can be approved?'
      />

      <Dialog open={approverDialogOpen} onOpenChange={setApproverDialogOpen}>
        <DialogContent className='sm:max-w-[440px]'>
          <DialogHeader>
            <DialogTitle>Assign an approver</DialogTitle>
            <DialogDescription>
              No approver is set for this policy. Choose the role responsible for
              approving it, or request approval without one. A workspace admin
              or owner can still approve.
            </DialogDescription>
          </DialogHeader>
          <div className='px-6'>
            <RoleSelect
              value={selectedApproverRoleId}
              roles={roles}
              onValueChange={(value) =>
                setSelectedApproverRoleId(value ?? '')
              }
              ariaLabel='Select an approver role'
              placeholder='Select an approver role'
            />
          </div>
          <DialogFooter>
            <button
              onClick={() => {
                setApproverDialogOpen(false)
                void submitApprovalRequest()
              }}
              disabled={approverDialogPending}
              className={`${actionButtonClass} border border-border bg-card text-text-body hover:bg-muted disabled:opacity-50`}
            >
              Request without approver
            </button>
            <button
              onClick={() => void handleConfirmApproverDialog()}
              disabled={!selectedApproverRoleId || approverDialogPending}
              className={`${actionButtonClass} border border-primary bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {approverDialogPending ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <Send className='h-3.5 w-3.5' />
              )}
              Assign &amp; Request
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className='sm:max-w-[440px]'>
          <DialogHeader>
            <DialogTitle>Rename policy</DialogTitle>
            <DialogDescription>
              Change the display and export name. The policy slot, controls, and
              evidence links stay the same.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2 px-6'>
            <Input
              autoFocus
              value={displayTitle}
              maxLength={200}
              onChange={(event) => setDisplayTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && displayTitle.trim()) {
                  event.preventDefault()
                  void updateDisplayTitle(displayTitle.trim())
                }
              }}
              aria-label='Policy name'
            />
            {item.canonicalTitle ? (
              <p className='text-[12px] text-text-subtle'>
                Default name: {item.canonicalTitle}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            {item.canonicalTitle && item.title !== item.canonicalTitle ? (
              <button
                type='button'
                onClick={() => void updateDisplayTitle(null)}
                disabled={updateDisplayTitleMutation.isPending}
                className={`${actionButtonClass} mr-auto border border-border bg-card text-text-body hover:bg-muted disabled:opacity-50`}
              >
                <RotateCcw className='h-3.5 w-3.5' />
                Reset to default
              </button>
            ) : null}
            <button
              type='button'
              onClick={() => setRenameDialogOpen(false)}
              disabled={updateDisplayTitleMutation.isPending}
              className={`${actionButtonClass} border border-border bg-card text-text-body hover:bg-muted disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={() => void updateDisplayTitle(displayTitle.trim())}
              disabled={
                !displayTitle.trim() ||
                displayTitle.trim() === item.title ||
                updateDisplayTitleMutation.isPending
              }
              className={`${actionButtonClass} border border-primary bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {updateDisplayTitleMutation.isPending ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <Save className='h-3.5 w-3.5' />
              )}
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
